package modysy

import modysy.datastructures.Vector2
import play.api.libs.json._
import play.api.libs.json.Reads._
import play.api.libs.functional.syntax._
import modysy.model._
import modysy.simulation.Behaviours
import modysy.simulation.pp._
import modysy.simulation.pp.SuccessConditions.LevelResult.LevelResult
import modysy.tools.{Dialog, NudgeTool, ObstaclePlacementTool, Tool}
import modysy.view.Color
import org.scalajs.dom
import org.scalajs.dom.raw.HTMLElement

import scala.collection.mutable
import scala.collection.mutable.ListBuffer

case class JsonToLevel(
    populationProtocol: String,
    winCondition: String,
    width: Int,
    height: Int,
    borderPoints: Array[Array[Int]],
    agentDensity: Double,
    agentRestitution: Double,
    objects: Array[JsonObject]
)

case class JsonObject(
    objType: String,
    position: Array[Int],
    linearVelocity: Array[Int],
    state: String,
    density: Double = 1,
    restitution: Double = 1,
    orientation: Double = 1
)

case class PortalData(entranceX: Double,
                      entranceY: Double,
                      exitX: Double,
                      exitY: Double)

object JsonParser {
  val portalReadsBuilder = ((JsPath \ "entrance_x").read[Double] and
    (JsPath \ "entrance_y").read[Double] and
    (JsPath \ "exit_x").read[Double] and
    (JsPath \ "exit_y").read[Double])

  implicit val portalReads: Reads[PortalData] =
    portalReadsBuilder.apply(PortalData.apply _)

  def deserialize(jsonString: String): Level = {
    val json = Json.parse(jsonString) // or JsonParser(source)
    val states_choice = (json \ "states").asOpt[Array[Int]] match {
      case Some(x) => x
      case _       => Array(1, 2, 3, 4)
    }
    val state_values: Array[State] = Array(
      State(
        "+L",
        Color.Green,
        Output("Pro"),
        Behaviours.chasingBehaviour("-", 2)
      ),
      State(
        "-L",
        Color.Red,
        Output("Con"),
        Behaviours.chasingBehaviour("+", 2)
      ),
      State(
        "+",
        Color.Green_Light,
        Output("Pro"),
        Behaviours.fleeingBehaviour("-L", 2)
      ),
      State(
        "-",
        Color.Red_Light,
        Output("Con"),
        Behaviours.fleeingBehaviour("+L", 2)
      )
    )
    val states_json: Array[State] = new Array[State](states_choice.length)

    for (i <- 0 until states_choice.length) {

      states_json(i) = state_values(states_choice(i) - 1)
    }
    val wincon_json = (json \ "winCondition").asOpt[String] match {
      case Some(x) => x
      case _       => "convertThemAll"
    }
    val text_blob = (json \ "text" \ "blob").asOpt[String] match {
      case Some(x) => x
      case _       => ""
    }
    val text_pos = (json \ "text" \ "position").asOpt[Array[Int]] match {
      case Some(x) => Vector2(x(0), x(1))
      case _       => Vector2(-1200, -2500)
    }
    val text_width = (json \ "text" \ "width").asOpt[Int] match {
      case Some(x) => x
      case _       => 120
    }
    val text_anchor = (json \ "text" \ "anchor").asOpt[String] match {
      case Some(x) => x
      case _       => "top"
    }
    val text_cooldown = (json \ "text" \ "cooldown").asOpt[Int] match {
      case Some(x) => x
      case _       => -1
    }
    val wincon = wincon_json match {
      case "convertThemAll" =>
        SuccessConditions.convertThemAll(states_json(0).output)
      case "tryToSurvive" =>
        SuccessConditions.tryToSurvive(states_json(0).output)
      case "leaderElection" =>
        SuccessConditions.tryToSurvive(states_json(0).output)
    }
    val coin_toggle = (json \ "coin").asOpt[String] match {
      case Some("true") => true
      case _            => false
    }
    val on_time_up = (json \ "on_time_up").asOpt[String] match {
      case Some("win") => SuccessConditions.LevelResult.win
      case _           => SuccessConditions.LevelResult.lose
    }
    val width_json = (json \ "width").as[Int]
    val height_json = (json \ "height").as[Int]
    val nudge_use = (json \ "nudge_use").asOpt[Int] match {
      case Some(x) => x
      case _       => 0
    }
    val box_use = (json \ "box_use").asOpt[Int] match {
      case Some(x) => x
      case _       => 0
    }
    val agentDensity = (json \ "agentDensity").asOpt[Double] match {
      case Some(x) => x
      case _       => 1.0
    }
    val timer = (json \ "timer").asOpt[Int] match {
      case Some(x) => x
      case _       => 120
    }
    val agentRestitution = (json \ "agentRestitution").asOpt[Double] match {
      case Some(x) => x
      case _       => 1.0
    }
    val obstacleRestitution =
      (json \ "obstacleRestitution").asOpt[Double] match {
        case Some(x) => x
        case _       => 1.0
      }

    def initialAgents = {
      var i = 0
      var bool = true
      val objects = new ListBuffer[GameObject]

      while (bool) {
        val typ = (json \ "objects" \ i \ "type").asOpt[String] match {
          case Some(x) => x
          case _       => "something is wrong"
        }
        val position: Array[Double] =
          (json \ "objects" \ i \ "position").asOpt[Array[Double]] match {
            case Some(x) => Array(x(0), x(1))
            case _       => Array(0.0, 0.0)
          }
        val linearVelocity: Array[Double] =
          (json \ "objects" \ i \ "linearVelocity").asOpt[Array[Double]] match {
            case Some(x) => x
            case _       => Array(0.0, 0.0)
          }
        val state =
          (json \ "objects" \ i \ "state").asOpt[String] match {
            case Some(x) => x
            case _ => {
              "+"
            }
          }
        val density: Double =
          (json \ "objects" \ i \ "density").asOpt[Double] match {
            case Some(x) => x
            case _       => agentDensity
          }
        val restitution: Double =
          (json \ "objects" \ i \ "restitution").asOpt[Double] match {
            case Some(x) => x
            case _ =>
              if (typ == "agent") {
                agentRestitution
              } else { // nice ternary operator doesn't work, thanks Scala!
                obstacleRestitution // or me just stupid
              }
          }
        val orientation: Double =
          (json \ "objects" \ i \ "orientation").asOpt[Double] match {
            case Some(x) => x
            case _       => 0.0
          }
        val angular: Double = 0.0

        val state_i = state match {
          case "+L" => 0
          case "-L" => 1
          case "+"  => 2
          case "-"  => 3
        }
        var obj: GameObject = typ match {
          case "agent" => //actually "agent", but until the obstacle conundrum is solved, will have to stay that way
            new Agent(
              new RigidBody(
                new Transform(Vector2(position(0), position(1)), orientation),
                new Velocity(
                  Vector2(linearVelocity(0), linearVelocity(1)),
                  angular
                ), //hardcoded (1,2) cuz no format yet again
                Material(density, restitution),
                Circle(states_json(state_i).deducedAgentRadius)
              ),
              states_json(state_i)
            )
          case _ => //"obstacle"
            val tmp = new Obstacle()
            tmp.body.pose = Transform
              .translate(Vector2(position(0), position(1)))
              .rotate(orientation)
            tmp
          //case _          => throw new RuntimeException
        }
        objects += obj
        i += 1
        (json \ "objects" \ i \ "type").asOpt[String] match {
          case Some(_) =>
          case None    => bool = false
        }
      }

      objects
    }

    val level = new Level {
      var dialog1 =
        new Dialog(text_pos, text_blob, text_width, text_cooldown, text_anchor)
      override var dialogs = ListBuffer(dialog1)

      /** The population protocol used in this level. */
      override val protocol: PopulationProtocol = new PopulationProtocol

      override var portals = {
        (json \ "portals") match {
          case l: JsDefined =>
            l.as[List[PortalData]]
              .map(pdata => {
                val entrance = new Portal(true)
                val exit = new Portal(false)

                entrance.body.pose.translate((pdata.entranceX, pdata.entranceY))
                exit.body.pose.translate((pdata.exitX, pdata.exitY))

                (entrance, exit)
              })
          case _: JsUndefined => Seq()
        }
      }

      val states: Array[State] = states_json
      protocol.addRuleSymmetric(
        Rule(states(0), states(1), states(2), states(3))
      )
      protocol.addRuleSymmetric(
        simulation.pp.Rule(states(0), states(3), states(2), states(0))
      )
      protocol.addRuleSymmetric(
        simulation.pp.Rule(states(2), states(1), states(1), states(3))
      )
      private val successCondition = wincon

      /** The current result of the level. */
      override def result: LevelResult = successCondition(objects)

      /** The width of the outer field. */
      override val width: Int = width_json

      /** The height of the outer field. */
      override val height: Int = height_json

      /**
        * The time (in seconds) the player has to complete the level
        */
      override val coin = coin_toggle
      override val time: Int = timer
      override val onTimeUp: LevelResult = on_time_up

      /** The inner field. */
      override val innerField: InnerField =
        new RectangularInnerField((0, 0), (1920, 1010))

      /** Tools available for the user */
      override def availableTools(controller: GameController): List[Tool] = {

        val toolOne = new ObstaclePlacementTool(controller)
        val toolTwo = new NudgeTool(controller)

        toolOne.uses = box_uses
        toolTwo.uses = nudge_uses

        List(
          //new AgentPlacementTool(controller, protocol.getStateByName("+L").get),
          //new AgentPlacementTool(controller, protocol.getStateByName("-L").get),
          toolOne,
          toolTwo
        )
      }

      /**
        * The list buffer containing all the objects present on the level, except for the borders of the inner field.
        * After the creation of a Level object, this buffer
        * contains the initial objects that are in the level design. The user of the Level object can append and delete
        * objects from this buffer. However be careful with storing references pointing to specific objects in this
        * buffer, as they will not point to objects present in the level after the resetObjects call.
        */
      override var objects: ListBuffer[GameObject] = new ListBuffer[GameObject]

      private def getInitialAgents: ListBuffer[GameObject] = {
        initialAgents
      }

      /** Restore the initial objects that are specified in the level design. */
      override def resetObjects(): Unit = {
        objects.clear()
        objects ++= makeBorders ++= getInitialAgents
      }

      /**
        * Display level specific messages to inform the player of something.
        */
      override def displayMessages(): Unit = ???

      override val box_uses = box_use
      override val nudge_uses = nudge_use

      private def makeBorders = {
        val verticalBox = Box(2, height)
        val horizontalBox = Box(width, 2)
        val makeBorder = (box: Box, position: Vector2) =>
          new FixedShapeGameObject(
            new RigidBody(
              Transform.translate(position),
              Velocity.zero,
              Material(density = Double.PositiveInfinity, restitution = 1),
              box
            ),
            "purple"
        )
        List(
          makeBorder(horizontalBox, Vector2(width.toDouble / 2, 1)),
          makeBorder(horizontalBox, Vector2(width.toDouble / 2, height - 1)),
          makeBorder(verticalBox, Vector2(1, height.toDouble / 2)),
          makeBorder(verticalBox, Vector2(width - 1, height.toDouble / 2))
        )
      }
    }
    return level
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/JsonParser.scala